import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Plane,
  Box,
  Camera,
  Users,
  Truck,
  Shield,
  ChevronRight,
  CheckCircle,
} from "lucide-react";

const ServicesPage = () => {
  const services = [
    {
      icon: Plane,
      title: "Aviation Readiness & Airfield Operations",
      description: "Complete aircraft support and ground operations for government and commercial aviation needs.",
      features: [
        "Aircraft support and maintenance coordination",
        "Ground operations management",
        "Ferry coordination services",
        "Airport-based deployment",
        "Aviation logistics support",
        "Readiness services",
      ],
      link: "/aviation",
      image: "https://images.unsplash.com/photo-1768896335994-4da6e40858f5?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
    {
      icon: Box,
      title: "Government Procurement & Logistics",
      description: "Mission-critical supply chain support for government agencies and prime contractors.",
      features: [
        "Government sourcing",
        "Packaging and preparation",
        "Rapid delivery services",
        "Mission-critical supply chain",
        "Procurement support",
        "Contract fulfillment",
      ],
      link: "/government-contracting",
      image: "https://images.unsplash.com/photo-1740914994657-f1cdffdc418e?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
    {
      icon: Users,
      title: "Tier-1 Veteran Deployment Teams",
      description: "100% veteran workforce ready for rapid response and expeditionary logistics operations.",
      features: [
        "Veteran manpower deployment",
        "Rapid response teams",
        "Expeditionary logistics",
        "Disaster response support",
        "Security personnel",
        "Mission-specific staffing",
      ],
      link: "/services",
      image: "https://images.unsplash.com/photo-1682623764612-4e942b7c8119?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
    {
      icon: Camera,
      title: "Aerial Photography & Drone Operations",
      description: "Government-grade aerial imaging and ISR flight operations for infrastructure and reconnaissance.",
      features: [
        "Aerial imaging services",
        "Infrastructure photography",
        "Site surveys",
        "Government-grade drone services",
        "ISR (Intelligence, Surveillance, Reconnaissance)",
        "Data collection and analysis",
      ],
      link: "/aerial-photography",
      image: "https://images.unsplash.com/photo-1640621202549-b813eb0a0720?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
    {
      icon: Truck,
      title: "Rapid Response & Critical Delivery",
      description: "Time-sensitive transport and emergency logistics with 72-hour mobilization capability.",
      features: [
        "Emergency logistics",
        "Time-sensitive transport",
        "Airport-to-door delivery",
        "Critical cargo handling",
        "72-hour mobilization",
        "CONUS/OCONUS deployment",
      ],
      link: "/logistics",
      image: "https://images.unsplash.com/photo-1768796373307-fc2f843660f8?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
  ];

  return (
    <div className="min-h-screen" data-testid="services-page">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#1a365d]" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">CAPABILITIES</p>
            <h1 className="font-heading text-4xl md:text-6xl text-white mb-6">
              OUR SERVICES
            </h1>
            <p className="text-slate-200 text-lg leading-relaxed">
              AMR Global Holdings LLC provides comprehensive aviation, logistics, and 
              rapid deployment services for government and commercial clients. Our veteran-led 
              teams deliver mission-critical support with military precision.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 bg-white" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div
                key={index}
                className={`grid md:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "md:flex-row-reverse" : ""
                }`}
                data-testid={`service-section-${index}`}
              >
                <div className={index % 2 === 1 ? "md:order-2" : ""}>
                  <div className="w-14 h-14 bg-[#1a365d] rounded-sm flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-[#c5a04a]" />
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl text-[#1a365d] mb-4">
                    {service.title}
                  </h2>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#c5a04a] flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={service.link}>
                    <Button className="btn-tactical btn-outline text-xs" data-testid={`service-learn-more-${index}`}>
                      Learn More
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
                <div className={index % 2 === 1 ? "md:order-1" : ""}>
                  <div className="relative">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="rounded-sm shadow-lg w-full h-[400px] object-cover"
                    />
                    <div className="absolute top-4 left-4 bg-[#c5a04a] px-4 py-2 rounded-sm">
                      <span className="font-heading text-[#1a365d] text-sm tracking-wider">
                        SERVICE {String(index + 1).padStart(2, "0")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NAICS Codes */}
      <section className="py-20 bg-slate-50 border-y border-slate-200" data-testid="naics-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#c5a04a] font-heading tracking-[0.3em] text-sm mb-4">COMPLIANCE</p>
            <h2 className="font-heading text-3xl md:text-4xl text-[#1a365d]">NAICS CODES</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { code: "481112", description: "Scheduled Freight Air Transportation" },
              { code: "481211", description: "Nonscheduled Chartered Freight Air Transportation" },
              { code: "489510", description: "Other Support Activities for Air Transportation" },
              { code: "541614", description: "Process, Physical Distribution, and Logistics Consulting Services" },
              { code: "561210", description: "Facilities Support Services" },
            ].map((naics, index) => (
              <Card key={index} className="card-tactical" data-testid={`naics-card-${index}`}>
                <CardContent className="p-6">
                  <p className="font-mono text-2xl text-[#c5a04a] mb-2">{naics.code}</p>
                  <p className="text-slate-600 text-sm">{naics.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1a365d]" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 text-[#c5a04a] mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
            MISSION-READY SUPPORT
          </h2>
          <p className="text-slate-200 text-lg mb-8">
            Contact us to discuss your aviation, logistics, or deployment requirements. 
            Our veteran teams are ready to support your mission.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="btn-tactical bg-[#c5a04a] text-[#1a365d] hover:bg-[#a38332] text-sm" data-testid="services-contact-btn">
                Request Capability Statement
              </Button>
            </Link>
            <Link to="/contact?type=contracting">
              <Button variant="outline" className="btn-tactical border-2 border-white text-white hover:bg-white hover:text-[#1a365d] text-sm" data-testid="services-inquiry-btn">
                Contracting Inquiry
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
